/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable indent */

import { useDatepickerMask } from '@/hooks/useDatepickerMask';
import { getDatepickerLocaleProps } from '@/utils/getDatepickerLocaleProps';
import { takeLocales } from '@/utils/takeLocales';
import AirDatepicker from 'air-datepicker';
import 'air-datepicker/air-datepicker.css';
import React, { Ref, useEffect, useRef } from 'react';
import { EpDatepickerProps, IDateOptions } from '../../interfaces';
import { EpInput } from '../EpInput';
import './EpDatepicker.scss';
import { getDatepickerConfig } from './utils/getDatepickerConfig';
import { handleOnChange } from './utils/handleOnChange';
import { resetSelectedDate } from './utils/resetSelectedDate';
import { onSaveDate } from './utils/onSaveDate';

// export const getValue = () => async (): Promise<string | null> => {
//   const selectedDate: Date | null = datepickerRef.current?.selectedDates[0] || null;
//   if (selectedDate === null) return null;
//   const formatedDate: Date = new Date(formatISO(selectedDate));
//   const currentTimeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
//   const utcDate: Date = zonedTimeToUtc(formatedDate, currentTimeZone);

//   return utcDate.toISOString();
// };

// export const setValue = () => async (newDate: Date): Promise<void> => {
//   datepickerRef.current?.selectDate(newDate || '');
// };

// export const clearValue = () => async (): Promise<void> => {
//   if (datepickerRef.current === null || datepickerRef.current === undefined) return;
//   datepickerRef.current.clear();
// };

export const dateOptions = ({ count, type }: IDateOptions) => {
  return {
    years: type === 'year' ? count : 0,
    months: type === 'month' ? count : 0,
    weeks: type === 'week' ? count : 0,
    days: type === 'day' ? count : 0,
  };
};

export const EpDatepicker: React.FC<EpDatepickerProps> = ({
  datepickerId,
  cultureCode,
  customClasses,
  isInlineDatepicker = false,
  isManualEntry = true,
  datepickerInputName,
  datepickerSize = 'md',
  datepickerOnChangeFunc,
  datepickerMinView = 'days',
  datepickerView = 'days',
  datepickerMaxDate,
  datepickerDateFormat,
  reactHookFormValue,
  reactHookFormOnChangeFunc,
  reactHookFormGetValues,
  inputHasIcon,
  inputIconName = 'RegularCalendarAlt',
  inputIsFullWidth,
  isoFormatDate = false,
}) => {
  const datepickerInstanceRef = useRef<AirDatepicker<HTMLElement> | null>(null);
  const { iMask, dateFormatByCultureCode, placeholderFormat } = useDatepickerMask({
    cultureCode,
    instanceRef: datepickerInstanceRef,
    maxDate: datepickerMaxDate,
  });

  const maskedInputRef = iMask?.ref as Ref<HTMLInputElement> | undefined;

  useEffect(() => {
    const language = takeLocales(cultureCode);
    const locale = getDatepickerLocaleProps(language);
    const configProps = {
      inline: isInlineDatepicker,
      view: datepickerView,
      minView: datepickerMinView,
      dateFormat: datepickerDateFormat,
      maxDate: new Date(),
      firstDay: 0,
      locale,
      cultureCode,
      selectedDates: reactHookFormGetValues ? [reactHookFormGetValues(datepickerInputName)] : [],
      onSelect: ({ formattedDate, date }: any) => {
        const finalDate = isoFormatDate ? date : formattedDate;

        handleOnChange({
          date: finalDate,
          onChange: datepickerOnChangeFunc,
          hookFormOnChange: reactHookFormOnChangeFunc
        });
      }
    };

    if (datepickerInstanceRef.current) {
      datepickerInstanceRef.current.update(getDatepickerConfig({ ...configProps }));
    } else {
      datepickerInstanceRef.current = new AirDatepicker(`#${datepickerId}`, getDatepickerConfig({ ...configProps }));
    }
  }, [cultureCode, datepickerId, datepickerOnChangeFunc, isInlineDatepicker, reactHookFormOnChangeFunc, reactHookFormGetValues, datepickerInputName, datepickerView, datepickerMinView, datepickerDateFormat, isoFormatDate]);

  return (
    <>
      {isInlineDatepicker ? (
        <div
          id={datepickerId}
          className={`ep-datepicker ${customClasses ? customClasses : ''}`}
        ></div>
      ) : (
        <EpInput
          inputId={datepickerId}
          inputSize={datepickerSize}
          inputName={datepickerInputName}
          {...(isManualEntry
            ? { ref: maskedInputRef }
            : { inputValue: reactHookFormValue })}
          inputPlaceholder={placeholderFormat}
          inputOnFocusFunc={(event: React.FocusEvent<HTMLInputElement>) => {
            onSaveDate({
              date: event?.target?.value,
              maxDate: datepickerMaxDate,
              dateFormatByCultureCode,
              onChange: datepickerOnChangeFunc,
              hookFormOnChange: reactHookFormOnChangeFunc,
            });
          }}
          onBlur={({ target: { value } }) => {
            onSaveDate({
              date: value,
              maxDate: datepickerMaxDate,
              dateFormatByCultureCode,
              onChange: datepickerOnChangeFunc,
              hookFormOnChange: reactHookFormOnChangeFunc,
            });
          }}
          onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;

            resetSelectedDate({
              date: target.value,
              placeholderFormat,
              instanceRef: datepickerInstanceRef
            });
          }}
          customClasses={`ep-datepicker ${customClasses ? customClasses : ''}`}
          inputHasIcon={inputHasIcon}
          inputIconName={inputIconName}
          inputIsFullWidth={inputIsFullWidth}
        />
      )}
    </>
  );
};
